type PlayerRow = Record<string, any>;

type MaxPrepsExportInput = {
  supplierId?: string;
  possessionPlayers: PlayerRow[];
  offensePlayers: PlayerRow[];
  goaliePlayers: PlayerRow[];
  faceoffPlayers: PlayerRow[];
  penaltyPlayers: PlayerRow[];
};

const MAXPREPS_FIELD_ORDER = [
  'Goals',
  'Assists',
  'TotalShots',
  'ShotsOnGoal',
  'GroundBalls',
  'Turnovers',
  'Takeaways',
  'UnforcedErrors',
  'FaceoffWon',
  'FaceoffAttempts',
  'MinutesPlayed',
  'GoalsAgainst',
  'Saves',
  'ShutOuts',
  'Wins',
  'Losses',
  'Ties',
  'Penalties',
  'PenaltyMinutes',
  'PenaltySeconds',
];

const NUMERIC_FIELDS = new Set(MAXPREPS_FIELD_ORDER);

const toJersey = (value: unknown) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const toNumber = (value: unknown) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const formatValue = (field: string, value: unknown) => {
  if (!NUMERIC_FIELDS.has(field)) return String(value ?? '');
  const numberValue = toNumber(value);
  return Number.isInteger(numberValue) ? String(numberValue) : String(Number(numberValue.toFixed(2)));
};

const hasValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return false;
  if (typeof value === 'number') return Number.isFinite(value) && value !== 0;
  if (typeof value === 'string') return value.trim() !== '' && Number(value) !== 0;
  return true;
};

const pipeSafe = (value: string) => value.replace(/\|/g, ' ');

const upsertPlayer = (playersByJersey: Map<string, PlayerRow>, row: PlayerRow) => {
  const jersey = toJersey(row.playerNumber);
  if (!jersey) return null;

  if (!playersByJersey.has(jersey)) {
    playersByJersey.set(jersey, { Jersey: jersey });
  }

  return playersByJersey.get(jersey) || null;
};

export const buildMaxPrepsLacrosseExport = ({
  supplierId,
  possessionPlayers,
  offensePlayers,
  goaliePlayers,
  faceoffPlayers,
  penaltyPlayers,
}: MaxPrepsExportInput) => {
  const playersByJersey = new Map<string, PlayerRow>();

  offensePlayers.forEach((player) => {
    const target = upsertPlayer(playersByJersey, player);
    if (!target) return;

    target.Goals = toNumber(target.Goals) + toNumber(player.goals);
    target.Assists = toNumber(target.Assists) + toNumber(player.assists);
    target.TotalShots = toNumber(target.TotalShots) + toNumber(player.shots);
    target.ShotsOnGoal = toNumber(target.ShotsOnGoal) + toNumber(player.goals) + toNumber(player.saved);
  });

  possessionPlayers.forEach((player) => {
    const target = upsertPlayer(playersByJersey, player);
    if (!target) return;

    target.GroundBalls = toNumber(target.GroundBalls) + toNumber(player.groundBalls);
    target.Turnovers = toNumber(target.Turnovers) + toNumber(player.turnovers);
    target.Takeaways = toNumber(target.Takeaways) + toNumber(player.causedTurnovers);
    target.UnforcedErrors = toNumber(target.UnforcedErrors) + toNumber(player.unforcedErrors);
  });

  faceoffPlayers.forEach((player) => {
    const target = upsertPlayer(playersByJersey, player);
    if (!target) return;

    target.FaceoffWon = toNumber(target.FaceoffWon) + toNumber(player.won);
    target.FaceoffAttempts = toNumber(target.FaceoffAttempts) + toNumber(player.faceoffs);
  });

  goaliePlayers.forEach((player) => {
    const target = upsertPlayer(playersByJersey, player);
    if (!target) return;

    target.MinutesPlayed = toNumber(target.MinutesPlayed) + toNumber(player.minutesPlayed);
    target.GoalsAgainst = toNumber(target.GoalsAgainst) + toNumber(player.goalsAllowed);
    target.Saves = toNumber(target.Saves) + toNumber(player.saved);
    target.ShutOuts = toNumber(target.ShutOuts) + toNumber(player.shutOuts);
    target.Wins = toNumber(target.Wins) + toNumber(player.wins);
    target.Losses = toNumber(target.Losses) + toNumber(player.losses);
    target.Ties = toNumber(target.Ties) + toNumber(player.ties);
  });

  penaltyPlayers.forEach((player) => {
    const target = upsertPlayer(playersByJersey, player);
    if (!target) return;

    target.Penalties = toNumber(target.Penalties) + toNumber(player.penalties);
    target.PenaltyMinutes = toNumber(target.PenaltyMinutes) + toNumber(player.penaltyMinutes);
    target.PenaltySeconds = toNumber(target.PenaltySeconds) + toNumber(player.penaltySeconds);
  });

  const players = Array.from(playersByJersey.values());
  const includedFields = MAXPREPS_FIELD_ORDER.filter((field) => players.some((player) => hasValue(player[field])));
  const headers = ['Jersey', ...includedFields];
  const lines = [headers.join('|')];

  players.forEach((player) => {
    lines.push(headers.map((field) => pipeSafe(formatValue(field, player[field]))).join('|'));
  });

  const trimmedSupplierId = supplierId?.trim();
  if (trimmedSupplierId) {
    lines.unshift(trimmedSupplierId);
  }

  return {
    content: lines.join('\r\n'),
    playerCount: players.length,
    fields: headers,
  };
};
